/**
 * Cargos y regiones: los dos catálogos que la vinculación elige en vez de
 * escribir.
 *
 * Antes el cargo era un enum cerrado en el código del front (`AMBASSADOR`,
 * `SUPERVISOR`, `COORDINATOR`) y la región un texto libre. Ahora los dos son
 * entidades con su id, y aquí no queda ni una lista de valores escrita a mano:
 * lo que hay en el selector es lo que devuelve el listado.
 *
 * Los dos catálogos comparten forma --código, nombre y cuántas vinculaciones
 * los usan-- y por eso comparten pantalla (`CatalogPanel`). Lo que cambia son
 * los nombres de las herramientas y las palabras, y eso es justo lo que
 * describe cada `Catalog`. Si el backend los separa algún día, se separan aquí
 * dejando de compartir el descriptor, sin tocar la pantalla.
 *
 * Va por las rutas DERIVADAS DEL CATÁLOGO (§6), las mismas que usa el agente:
 * no hay una segunda puerta a la escritura.
 */
import { queryOptions } from "@tanstack/react-query";

import { request } from "../lib/api-client.ts";
import { API_BASE } from "../lib/api-routes.ts";
import type { operations } from "../lib/api-types.ts";
import { PERMISSIONS, type Permission } from "../lib/permissions.ts";

type PositionList =
  operations["list_positions"]["responses"][200]["content"]["application/json"]["data"];

type RegionList =
  operations["list_regions"]["responses"][200]["content"]["application/json"]["data"];

/**
 * La forma que la interfaz necesita de una entrada de catálogo.
 *
 * Se declara aquí y las dos respuestas del backend tienen que encajar: si una
 * de las dos pierde un campo, esto deja de compilar en vez de enseñar una
 * columna vacía.
 */
export interface CatalogEntry {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  /** Cuántas vinculaciones lo usan. Es lo que decide si se puede borrar. */
  readonly enrollments: number;
  readonly createdAt: string;
}

export interface CatalogList {
  readonly entries: readonly CatalogEntry[];
  readonly total: number;
}

/** Lo que se rellena en el formulario, igual para los dos. */
export interface CatalogInput {
  readonly code: string;
  readonly name: string;
}

/** Las palabras de cada catálogo. En español el género obliga a escribirlas. */
export interface CatalogCopy {
  readonly title: string;
  readonly subtitle: string;
  /** El botón de alta: "Cargo nuevo", "Región nueva". */
  readonly newAction: string;
  readonly editTitle: string;
  readonly deleteTitle: string;
  readonly deleteQuestion: (name: string) => string;
  /** El aviso cuando hay vinculaciones colgando. */
  readonly inUse: (enrollments: number) => string;
  readonly empty: string;
  readonly codeHint: string;
  readonly nameHint: string;
}

/**
 * El código que acepta el backend: minúsculas, números y guiones.
 *
 * La regla es SUYA --la declara el esquema de la herramienta-- y esto solo
 * evita el viaje de ida y vuelta para enterarse. El servidor sigue validando:
 * esto no es la validación, es la cortesía.
 */
const CODE_PATTERN = /^[a-z0-9-]+$/;

export const isValidCode = (code: string): boolean =>
  CODE_PATTERN.test(code.trim());

/** Se puede guardar cuando hay nombre y el código tiene la forma buena. */
export const canSaveCatalogEntry = (input: CatalogInput): boolean =>
  input.name.trim() !== "" && isValidCode(input.code);

export interface Catalog {
  readonly queryKey: readonly string[];
  /** El permiso de crear, editar y borrar (§10.2). Solo decide qué se enseña. */
  readonly writePermission: Permission;
  readonly copy: CatalogCopy;
  readonly list: () => Promise<CatalogList>;
  readonly create: (input: CatalogInput) => Promise<unknown>;
  readonly update: (id: string, input: CatalogInput) => Promise<unknown>;
  readonly remove: (id: string) => Promise<unknown>;
}

const command = (name: string, body: unknown): Promise<unknown> =>
  request(`${API_BASE}/commands/${name}`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const POSITIONS: Catalog = {
  queryKey: ["positions"],
  writePermission: PERMISSIONS.positionsWrite,
  copy: {
    title: "Cargos",
    subtitle: "El cargo con el que alguien queda vinculado en un mes.",
    newAction: "Cargo nuevo",
    editTitle: "Editar el cargo",
    deleteTitle: "Borrar el cargo",
    deleteQuestion: (name) => `¿Borrar el cargo “${name}”?`,
    inUse: (enrollments) =>
      enrollments === 1
        ? "Hay 1 vinculación con este cargo."
        : `Hay ${enrollments.toLocaleString("es")} vinculaciones con este cargo.`,
    empty: "Todavía no hay cargos. El primero se crea aquí.",
    codeHint: "En minúsculas y con guiones, como jefe-de-zona",
    nameHint: "Como lo llama la gente, como Jefe de zona",
  },
  list: async () => {
    const data = await request<PositionList>(
      `${API_BASE}/queries/list_positions`,
    );
    return { entries: data.positions, total: data.total };
  },
  create: (input) => command("create_position", input),
  update: (id, input) =>
    command("update_position", { positionId: id, ...input }),
  remove: (id) => command("delete_position", { positionId: id }),
};

export const REGIONS: Catalog = {
  queryKey: ["regions"],
  writePermission: PERMISSIONS.regionsWrite,
  copy: {
    title: "Regiones",
    subtitle: "Las regiones entre las que se reparte el padrón.",
    newAction: "Región nueva",
    editTitle: "Editar la región",
    deleteTitle: "Borrar la región",
    deleteQuestion: (name) => `¿Borrar la región “${name}”?`,
    inUse: (enrollments) =>
      enrollments === 1
        ? "Hay 1 vinculación en esta región."
        : `Hay ${enrollments.toLocaleString("es")} vinculaciones en esta región.`,
    empty: "Todavía no hay regiones. La primera se crea aquí.",
    codeHint: "En minúsculas y con guiones, como santo-domingo",
    nameHint: "Como la llama la gente, como Santo Domingo",
  },
  list: async () => {
    const data = await request<RegionList>(`${API_BASE}/queries/list_regions`);
    return { entries: data.regions, total: data.total };
  },
  create: (input) => command("create_region", input),
  update: (id, input) => command("update_region", { regionId: id, ...input }),
  remove: (id) => command("delete_region", { regionId: id }),
};

/**
 * El listado de un catálogo.
 *
 * Lo usan su pantalla y los selectores de la vinculación, y es la misma
 * consulta: el selector no puede ofrecer algo que la pantalla no enseñe.
 */
export const catalogQuery = (catalog: Catalog) =>
  queryOptions({
    queryKey: catalog.queryKey,
    queryFn: catalog.list,
  });
